import { lazy, Suspense, useEffect } from 'react';

import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Box, CircularProgress } from '@mui/material';

import { normalizeRepeatedAmpersandsInSearch } from 'utils/normalizeRepeatedAmpersandsInSearch';
import { clearOnboardingJourneyState } from 'utils/onboardingFlowStorage';

import AdminRouteTree from './adminRoutes';
import { sharedPaths } from './constants/sharedPaths';
import { userPaths } from './constants/userPaths';
import UserRouteTree from './userRoutes';

const Landing = lazy(() => import('pages/landing'));
const CashbackCalculatorPage = lazy(() => import('pages/cashbackCalculator'));

const RouteLoadingFallback = () => (
  <Box
    sx={{
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#F5F6F7',
    }}
    aria-label="Loading"
  >
    <CircularProgress size={40} />
  </Box>
);

const FinchConnectionLegacyRedirect = () => {
  const { search } = useLocation();
  return (
    <Navigate
      to={{
        pathname: userPaths.finchConnection,
        search: normalizeRepeatedAmpersandsInSearch(search),
      }}
      replace
    />
  );
};

const LandingLegacyRedirect = () => {
  const { search } = useLocation();
  const params = new URLSearchParams(normalizeRepeatedAmpersandsInSearch(search));
  if (!params.get('step')) {
    params.set('step', '1');
  }

  return <Navigate to={{ pathname: userPaths.ftue, search: `?${params.toString()}` }} replace />;
};

const useOnboardingJourneyReset = (): void => {
  const { pathname } = useLocation();

  useEffect(() => {
    if (!pathname.startsWith(userPaths.onboarding)) {
      clearOnboardingJourneyState();
    }
  }, [pathname]);
};

const LegacyRouteRedirects = (
  <>
    <Route path={sharedPaths.legacyLogin} element={<Navigate to={userPaths.signIn} replace />} />
    <Route path={sharedPaths.legacySignIn} element={<Navigate to={userPaths.signIn} replace />} />
    <Route
      path={sharedPaths.legacySignInPhone}
      element={<Navigate to={userPaths.signInPhone} replace />}
    />
    <Route
      path={sharedPaths.legacySignInOtp}
      element={<Navigate to={userPaths.signInOtp} replace />}
    />
    <Route path={sharedPaths.legacyLanding} element={<LandingLegacyRedirect />} />
    <Route path={userPaths.landing} element={<LandingLegacyRedirect />} />
    <Route path={sharedPaths.legacyFtue} element={<Navigate to={userPaths.ftue} replace />} />
    <Route path={sharedPaths.legacyTerms} element={<Navigate to={userPaths.terms} replace />} />
    <Route
      path={sharedPaths.legacyLinkExpired}
      element={<Navigate to={userPaths.linkExpired} replace />}
    />
    <Route path={sharedPaths.legacyEndUser} element={<Navigate to={userPaths.endUser} replace />} />
    <Route
      path={sharedPaths.legacyConfirmCashback}
      element={<Navigate to={userPaths.confirmCashback} replace />}
    />
    <Route
      path={sharedPaths.legacyCashOut}
      element={<Navigate to={userPaths.hsaPaydays} replace />}
    />
    <Route
      path={sharedPaths.legacyHsaPaydays}
      element={<Navigate to={userPaths.hsaPaydays} replace />}
    />
    <Route
      path={sharedPaths.legacyExpenses}
      element={<Navigate to={userPaths.expenses} replace />}
    />
    <Route
      path={sharedPaths.legacyNoExpensesRemaining}
      element={<Navigate to={userPaths.noExpensesRemaining} replace />}
    />
    <Route
      path={sharedPaths.legacyNoExpenseFound}
      element={<Navigate to={userPaths.noExpenseFound} replace />}
    />
    <Route path={sharedPaths.legacyFinchConnection} element={<FinchConnectionLegacyRedirect />} />
    <Route
      path={sharedPaths.legacyPiiVerification}
      element={<Navigate to={userPaths.piiVerification} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboarding}
      element={<Navigate to={userPaths.onboardingEmailVerification} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingEmailVerification}
      element={<Navigate to={userPaths.onboardingEmailVerification} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingPhoneVerification}
      element={<Navigate to={userPaths.onboardingPhoneVerification} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingOtpVerification}
      element={<Navigate to={userPaths.onboardingOtpVerification} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingCompleteProfile}
      element={<Navigate to={userPaths.onboardingCompleteProfile} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingPersonalDetails}
      element={<Navigate to={userPaths.onboardingPersonalDetails} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingFindExpenses}
      element={<Navigate to={userPaths.onboardingFindExpenses} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingFindExpensesSuccess}
      element={<Navigate to={userPaths.onboardingFindExpensesSuccess} replace />}
    />
    <Route
      path={sharedPaths.legacyOnboardingFindExpensesProviders}
      element={<Navigate to={userPaths.onboardingFindExpensesProviders} replace />}
    />
  </>
);

const AppRoutes = () => {
  useOnboardingJourneyReset();

  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <Routes>
        <Route path={sharedPaths.root} element={<Landing />} />
        <Route path={sharedPaths.cashbackCalculator} element={<CashbackCalculatorPage />} />
        {AdminRouteTree}
        {UserRouteTree}
        {LegacyRouteRedirects}
        <Route path="*" element={<Navigate to={userPaths.signIn} replace />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
